import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PriorityStatus } from "@/types";
import { ReactNode } from "react";
import { PRIORITY_STATUS_LABEL, PRIORITY_STATUS_TOOLTIP } from "./priorityCardTokens";

interface PriorityStatusTooltipProps {
  status?: PriorityStatus | null;
  children: ReactNode;
  /** Layout classes for the trigger wrapper — pass whatever the badge needed from its parent flex row. */
  className?: string;
}

/**
 * Hover explanation for a status badge/chip. Statuses without tooltip copy (Focus, Queue, Done,
 * On Hold) render their children untouched, so this is safe to wrap around any status badge.
 *
 * Renders its own `<span>` trigger instead of `asChild`-ing the badge: `Badge` is a plain function
 * component, so Radix has no ref to anchor the tooltip to.
 */
export const PriorityStatusTooltip = ({ status, children, className }: PriorityStatusTooltipProps) => {
  const tooltip = status ? PRIORITY_STATUS_TOOLTIP[status] : undefined;
  if (!status || !tooltip) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex", className)}>{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">
        <p className="text-xs font-semibold">{PRIORITY_STATUS_LABEL[status]}</p>
        <p className="mt-1 text-xs text-muted-foreground">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};
